import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge } from "react-bootstrap";
import Helmet from "react-helmet";
import moment from "moment";
import "moment/locale/fr";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Layout from "app/universal/layout/Layout";
import AlphaBanner from "app/orderTool/components/AlphaBanner";
import LeMoulinApi from "app/universal/utils/LeMoulinApi";

/**
 * Planning client (alpha `planning_client`, Trello 9rOG1MXi).
 * À gauche l'édition par l'équipe, à droite l'aperçu de la page que reçoit le client.
 * Le déroulé part des horaires du devis ; l'équipe l'ajuste puis envoie le lien au client,
 * qui valide ou demande une modification jusqu'à J-7.
 */

const STATUS_VARIANTS = {
  draft: "secondary",
  sent: "info",
  change_requested: "warning",
  validated: "success",
};

// L'intercepteur axios (state/apiService) rend déjà le JSON ; on accepte les deux formes.
const unwrap = (res) => (res && res.data && res.data.orderId ? res.data : res);

const newLine = (date) => ({
  id: Math.random().toString(16).slice(2, 14),
  date: date || null,
  label: "",
  detail: "",
  start: null,
  end: null,
  note: "",
  visible: true,
});

const formatValue = (key, info) => {
  const value = info?.[key];
  if (!value) return null;
  switch (key) {
    case "date":
      return moment(value).format("dddd D MMMM YYYY");
    case "guests":
      return `${value} personnes`;
    case "access":
      return value.length ? value.join(" · ") : null;
    case "onsite_contact":
      return [value.firstname, value.lastname, value.phone, value.role].filter(Boolean).join(" · ") || null;
    case "account_manager":
      return [`${value.firstname} ${value.lastname || ""}`.trim(), value.phone].filter(Boolean).join(" · ");
    case "logistics":
      return [value.firstname, value.title, value.phone || value.email].filter(Boolean).join(" · ");
    default:
      return value;
  }
};

const ClientPlanning = () => {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [hiddenFields, setHiddenFields] = useState([]);
  const [internalNotes, setInternalNotes] = useState("");
  const [handledIds, setHandledIds] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);

  const hydrate = useCallback((payload) => {
    setData(payload);
    setLines(payload.lines || []);
    setHiddenFields(payload.hiddenFields || []);
    setInternalNotes(payload.internalNotes || "");
    setHandledIds((payload.clientRequests || []).filter((r) => r.handled).map((r) => r.id));
    setIsDirty(false);
    setPreviewKey((k) => k + 1);
  }, []);

  useEffect(() => {
    LeMoulinApi.request.order.clientPlanning
      .get(orderId)
      .then((res) => hydrate(unwrap(res)))
      .catch((e) => {
        console.error("Planning client", e);
        toast.error("Impossible de charger le planning");
      });
  }, [orderId, hydrate]);

  useEffect(() => {
    const warn = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const touch = (fn) => (...args) => {
    fn(...args);
    setIsDirty(true);
  };

  const updateLine = touch((index, field, value) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  });
  const moveLine = touch((index, delta) => {
    setLines((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  });
  const removeLine = touch((index) => setLines((prev) => prev.filter((_, i) => i !== index)));
  const addLine = touch(() => setLines((prev) => [...prev, newLine(data?.eventInfo?.date)]));
  const toggleField = touch((key) =>
    setHiddenFields((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  );
  const toggleHandled = touch((id) =>
    setHandledIds((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]))
  );

  const resetFromQuotation = () => {
    swal({
      title: "Repartir des horaires du devis ?",
      text: "Le déroulé actuel sera remplacé par celui calculé depuis le devis et la feuille de route.",
      buttons: ["Annuler", "Remplacer"],
    }).then((ok) => {
      if (ok) {
        setLines(data.defaultLines);
        setIsDirty(true);
      }
    });
  };

  const payload = () => ({
    lines: lines.filter((l) => (l.label || "").trim() !== ""),
    hiddenFields,
    internalNotes,
    handledRequestIds: handledIds,
  });

  const save = async () => {
    if (lines.some((l) => !(l.label || "").trim())) {
      toast.warning("Les lignes sans étape ne sont pas enregistrées.");
    }
    setIsSaving(true);
    try {
      const res = unwrap(await LeMoulinApi.request.order.clientPlanning.put(orderId, payload()));
      hydrate(res);
      toast.success("Planning enregistré");
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur à l'enregistrement");
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const send = async () => {
    const resend = data.status !== "draft";
    const ok = await swal({
      title: resend ? "Renvoyer le planning au client ?" : "Envoyer le planning au client ?",
      text:
        (resend ? "Le client devra le valider à nouveau. " : "") +
        "Le lien sera copié : collez-le dans votre mail au client.",
      buttons: ["Annuler", resend ? "Renvoyer" : "Envoyer"],
    });
    if (!ok) return;

    const saved = await save();
    if (!saved) return;
    try {
      const res = unwrap(await LeMoulinApi.request.order.clientPlanning.send(orderId));
      hydrate(res);
      await copyLink(res.publicUrl);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur à l'envoi");
    }
  };

  const copyLink = async (url) => {
    const link = url || data?.publicUrl;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Lien client copié");
    } catch (e) {
      swal({ title: "Lien client", text: link });
    }
  };

  if (!data) {
    return (
      <Layout>
        <Container className="py-5 text-center text-muted">Chargement…</Container>
      </Layout>
    );
  }

  const info = data.eventInfo || {};
  // Référence de la prestation (C2/15475) d'abord, puis le numéro P, qui ne vaut qu'avec sa date.
  const ref = [data.orderNumber, data.numbering].filter(Boolean).join(" · ");
  const previewUrl = data.publicUrl ? `${data.publicUrl}?apercu=1` : null;
  const openRequests = (data.clientRequests || []).filter((r) => !handledIds.includes(r.id));

  return (
    <Layout>
      <Helmet>
        <title>{`Planning client ${ref}`}</title>
      </Helmet>
      <Container fluid style={{ padding: "20px 20px 100px" }}>
        <AlphaBanner feature="Planning client" contactConnecteam />
        {!data.clientAccessEnabled && (
          <div className="alert alert-secondary py-2" style={{ fontSize: 14 }}>
            <strong>Pas encore envoyé aux clients.</strong> Le planning se prépare et se relit ici, avec l'aperçu de la page client à droite ;
            le lien n'est pas encore ouvert aux clients.
          </div>
        )}

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: 12 }}>
          <div>
            <h2 className="mb-1" style={{ fontSize: 24 }}>
              Planning client – {ref}
              {info.company ? ` · ${info.company}` : ""}
            </h2>
            <div className="text-muted">
              {info.date && moment(info.date).format("dddd D MMMM YYYY")}
              {data.lockDate && (
                <>
                  {" · "}
                  {data.locked ? (
                    <strong className="text-danger">modifications client fermées (J-7)</strong>
                  ) : (
                    <>modifications client jusqu'au {moment(data.lockDate).subtract(1, "day").format("DD/MM")} inclus</>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <Badge variant={STATUS_VARIANTS[data.status] || "secondary"} style={{ fontSize: 14, padding: "6px 10px" }}>
              {data.statusLabel}
            </Badge>
            {data.validatedAt && <small className="text-muted">le {moment(data.validatedAt).format("DD/MM à HH:mm")}</small>}
          </div>
        </div>

        <Row>
          <Col xl={7} className="mb-4">
            {(data.clientRequests || []).length > 0 && (
              <Card className="mb-3" border={openRequests.length ? "warning" : undefined}>
                <Card.Header style={{ background: openRequests.length ? "#fff6e0" : undefined }}>
                  <strong>Demandes du client</strong>{" "}
                  {openRequests.length ? `(${openRequests.length} à traiter)` : <small className="text-muted">(toutes traitées)</small>}
                </Card.Header>
                <Card.Body className="py-2">
                  {(data.clientRequests || []).map((request) => (
                    <Form.Check
                      key={request.id}
                      id={`request-${request.id}`}
                      className="py-2 border-bottom"
                      checked={handledIds.includes(request.id)}
                      onChange={() => toggleHandled(request.id)}
                      label={
                        <span style={{ opacity: handledIds.includes(request.id) ? 0.5 : 1 }}>
                          <small className="text-muted">
                            {moment(request.at).format("DD/MM HH:mm")}
                            {request.lineLabel ? ` · ${request.lineLabel}` : " · Général"}
                          </small>
                          <br />
                          {request.message}
                        </span>
                      }
                    />
                  ))}
                  <small className="text-muted d-block mt-2">
                    Cochez une demande quand elle est traitée, puis renvoyez le planning au client pour qu'il le valide.
                  </small>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between">
                <strong>Détails de l'événement</strong>
                <small className="text-muted">Visible client</small>
              </Card.Header>
              <Card.Body className="py-1">
                <Table size="sm" className="mb-1" style={{ fontSize: 14 }}>
                  <tbody>
                    {Object.entries(data.fields).map(([key, label]) => {
                      const value = formatValue(key, info);
                      return (
                        <tr key={key}>
                          <td className="text-muted" style={{ width: 220 }}>{label}</td>
                          <td>{value || <span className="text-danger">non renseigné</span>}</td>
                          <td className="text-right" style={{ width: 60 }}>
                            <Form.Check
                              id={`field-${key}`}
                              checked={!hiddenFields.includes(key)}
                              onChange={() => toggleField(key)}
                              aria-label={`${label} visible par le client`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <small className="text-muted d-block pb-2">
                  Ces informations viennent du devis et de la feuille de route : c'est là qu'on les corrige.
                  {data.missingCustomerFields > 0 && (
                    <>
                      {" "}
                      <strong className="text-warning">
                        {data.missingCustomerFields} information(s) manquante(s) côté client
                      </strong>{" "}
                      : la page client renvoie vers le formulaire « informations manquantes ».
                    </>
                  )}
                </small>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Déroulé global de l'événement</strong>
                <Button size="sm" variant="link" onClick={resetFromQuotation}>
                  Repartir des horaires du devis
                </Button>
              </Card.Header>
              <Card.Body className="p-0" style={{ overflowX: "auto" }}>
                <Table size="sm" className="mb-0" style={{ fontSize: 13, minWidth: 860, tableLayout: "fixed" }}>
                  <thead>
                    <tr className="text-muted">
                      <th style={{ width: 128 }}>Date</th>
                      <th style={{ width: 96 }}>Début</th>
                      <th style={{ width: 96 }}>Fin</th>
                      <th style={{ minWidth: 240 }}>Étape / détail lu par le client</th>
                      <th style={{ width: 150 }}>Point d'attention (interne)</th>
                      <th style={{ width: 52 }} title="Visible client">Client</th>
                      <th style={{ width: 100 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, index) => (
                      <tr key={line.id} style={{ background: line.visible ? "#f3f9f4" : undefined }}>
                        <td>
                          <Form.Control size="sm" type="date" value={line.date || ""} onChange={(e) => updateLine(index, "date", e.target.value || null)} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="time" value={line.start || ""} onChange={(e) => updateLine(index, "start", e.target.value || null)} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="time" value={line.end || ""} onChange={(e) => updateLine(index, "end", e.target.value || null)} />
                        </td>
                        <td>
                          <Form.Control size="sm" value={line.label} placeholder="Étape" style={{ fontWeight: 600 }} onChange={(e) => updateLine(index, "label", e.target.value)} />
                          <Form.Control size="sm" className="mt-1" value={line.detail || ""} placeholder="Détail pour le client (facultatif)" onChange={(e) => updateLine(index, "detail", e.target.value)} />
                        </td>
                        <td>
                          <Form.Control size="sm" as="textarea" rows={2} value={line.note || ""} placeholder="Interne" onChange={(e) => updateLine(index, "note", e.target.value)} />
                        </td>
                        <td className="text-center align-middle">
                          <Form.Check
                            id={`line-visible-${line.id}`}
                            checked={!!line.visible}
                            onChange={(e) => updateLine(index, "visible", e.target.checked)}
                            aria-label="Visible par le client"
                          />
                        </td>
                        <td className="text-nowrap align-middle" style={{ width: 96 }}>
                          <Button size="sm" variant="light" disabled={index === 0} onClick={() => moveLine(index, -1)} title="Monter">↑</Button>
                          <Button size="sm" variant="light" disabled={index === lines.length - 1} onClick={() => moveLine(index, 1)} title="Descendre">↓</Button>
                          <Button size="sm" variant="light" className="text-danger" onClick={() => removeLine(index)} title="Supprimer">✕</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="p-2">
                  <Button size="sm" variant="outline-primary" onClick={addLine}>+ Ajouter une ligne</Button>
                  <small className="text-muted ml-3">Les lignes en vert sont visibles par le client, dans cet ordre.</small>
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header><strong>Notes internes</strong> <small className="text-muted">(jamais visibles par le client)</small></Card.Header>
              <Card.Body>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={internalNotes}
                  onChange={(e) => {
                    setInternalNotes(e.target.value);
                    setIsDirty(true);
                  }}
                  placeholder="Brief équipe, vigilance accès…"
                />
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <div style={{ position: "sticky", top: 16 }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Ce que voit le client</strong>
                {previewUrl && (
                  <Button size="sm" variant="link" href={previewUrl} target="_blank">Ouvrir dans un onglet</Button>
                )}
              </div>
              {previewUrl ? (
                <>
                  {isDirty && <small className="text-warning d-block mb-1">Aperçu à jour après enregistrement.</small>}
                  <iframe
                    key={previewKey}
                    ref={iframeRef}
                    title="Aperçu de la page client"
                    src={previewUrl}
                    style={{ width: "100%", height: "calc(100vh - 140px)", border: "1px solid #dee2e6", borderRadius: 8, background: "#f6f7f4" }}
                  />
                </>
              ) : (
                <Card body className="text-muted">Enregistrez le brouillon pour voir l'aperçu de la page client.</Card>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <div
        className="no-print"
        style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#fff", borderTop: "1px solid #dee2e6", padding: "10px 20px", zIndex: 1000, display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}
      >
        {isDirty && <span className="text-warning align-self-center mr-auto">Modifications non enregistrées</span>}
        {!data.clientAccessEnabled && (
          <span className="text-muted align-self-center">Alpha : rien n'est envoyé au client, seul l'aperçu est disponible.</span>
        )}
        {data.clientAccessEnabled && data.publicUrl && data.status !== "draft" && (
          <Button variant="outline-secondary" onClick={() => copyLink()}>Copier le lien client</Button>
        )}
        <Button variant="outline-primary" onClick={save} disabled={isSaving}>
          Enregistrer {data.status === "draft" ? "le brouillon" : ""}
        </Button>
        {data.clientAccessEnabled && (
          <Button variant="success" onClick={send} disabled={isSaving}>
            {data.status === "draft" ? "Envoyer au client pour validation" : "Renvoyer au client pour validation"}
          </Button>
        )}
      </div>
    </Layout>
  );
};

export default ClientPlanning;
